import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import { MainLayout } from './components/layout';
import { BrandProvider } from './components/BrandProvider';
import { ThemeProvider } from './components/ThemeProvider';
import { I18nProvider } from './components/I18nProvider';
import {
  LoginPage,
  RegisterPage,
  DashboardPage,
  CustomersPage,
  PaymentsPage,
  CollectedPage,
  PersonnelPage,
  SettingsPage,
  WhatsAppPage,
  ProfilePage,
  LegalPage,
  LandingPage,
  BarcodeSearchPage,
} from './pages';
import SuspendedPage from './pages/SuspendedPage';
import TrialExpiredPage from './pages/TrialExpiredPage';
import {
  AdminDashboardPage,
  AdminCompaniesPage,
  AdminUsersPage,
  AdminMessagesPage,
  AdminSettingsPage,
  AdminTemplatesPage,
  AdminPackagesPage,
  AdminPaymentMethodsPage,
  AdminWhatsAppPage,
  AdminSecurityPage,
  AdminProductsPage,
  AdminOrdersPage,
  AdminDealersPage,
  AdminIntegrationsPage,
  AdminPaymentTrackingPage,
  AdminPersonnelPage,
  AdminShopSettingsPage,
} from './pages/admin';
import { DealerDashboard, DealerOrders, DealerCart, DealerShop, DealerPayments, DealerAccount } from './pages/dealer';
import { DealerLayout } from './components/layout/DealerLayout';
import { useAuthStore } from './store';

function PaymentResultPage({ status }) {
  const navigate = useNavigate();
  React.useEffect(() => {
    const timer = setTimeout(() => navigate('/login'), 5000);
    return () => clearTimeout(timer);
  }, [navigate]);
  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <div className="text-center max-w-md space-y-4">
        {status === 'success' ? (
          <>
            <div className="w-16 h-16 mx-auto rounded-full bg-green-100 flex items-center justify-center">
              <svg className="w-8 h-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
            </div>
            <h1 className="text-2xl font-bold text-green-700">Ödeme Başarılı!</h1>
            <p className="text-muted-foreground">Ödemeniz başarıyla alınmıştır. Teşekkür ederiz! Aboneliğiniz aktif edildi.</p>
          </>
        ) : (
          <>
            <div className="w-16 h-16 mx-auto rounded-full bg-red-100 flex items-center justify-center">
              <svg className="w-8 h-8 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
            </div>
            <h1 className="text-2xl font-bold text-red-700">Ödeme Başarısız</h1>
            <p className="text-muted-foreground">Ödeme işlemi tamamlanamadı. Lütfen tekrar deneyin veya farklı bir kart kullanın.</p>
          </>
        )}
        <p className="text-sm text-muted-foreground">5 saniye sonra giriş sayfasına yönlendirileceksiniz...</p>
        <button onClick={() => navigate('/login')} className="text-primary font-medium hover:underline text-sm">Giriş Sayfasına Git</button>
      </div>
    </div>
  );
}
// Personnel subscription expired page
function SubscriptionExpiredPersonnelPage() {
  const { logout } = useAuthStore();
  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4" data-testid="subscription-expired-personnel">
      <div className="w-full max-w-md rounded-xl border bg-card p-8 text-center shadow-lg">
        <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-amber-100">
          <svg className="h-10 w-10 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
        </div>
        <h1 className="text-2xl font-bold text-amber-700">Abonelik Süresi Doldu</h1>
        <p className="mt-3 text-muted-foreground">
          Firmanızın abonelik süresi dolmuştur. Sistemi kullanmaya devam etmek için firma yöneticinizin abonelik yenilemesi gerekmektedir.
        </p>
        <div className="mt-6 rounded-lg bg-muted/50 p-4 text-sm text-muted-foreground">
          Lütfen firma yöneticinize başvurun.
        </div>
        <button onClick={logout} className="mt-6 w-full rounded-lg border bg-background px-4 py-2.5 text-sm font-medium hover:bg-muted transition-colors" data-testid="expired-logout-btn">
          Çıkış Yap
        </button>
      </div>
    </div>
  );
}

import './App.css';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, isSuspended, isTrialExpired, user } = useAuthStore();
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (isSuspended) {
    return <SuspendedPage />;
  }

  // Trial/subscription expired handling
  if (isTrialExpired) {
    // Personnel: show "subscription expired" message
    if (user?.role === 'personnel') {
      return <SubscriptionExpiredPersonnelPage />;
    }
    // Company admin: allow access but will be restricted to settings via sidebar
    // (handled in route guard below)
  }
  
  return children;
};

// Route guard for expired companies - only allow settings page
const ExpiredRouteGuard = ({ children, allowWhenExpired }) => {
  const { isTrialExpired, user } = useAuthStore();
  if (isTrialExpired && user?.role !== 'super_admin' && user?.role !== 'personnel' && !allowWhenExpired) {
    return <Navigate to="/settings" replace />;
  }
  return children;
};

// Landing Route - show barcode search if not authenticated, redirect based on role if authenticated
const LandingRoute = () => {
  const { isAuthenticated, user } = useAuthStore();
  
  if (isAuthenticated) {
    if (user?.role === 'super_admin' || user?.role === 'personnel') {
      return <Navigate to="/admin" replace />;
    } else if (user?.role === 'dealer') {
      return <Navigate to="/dealer" replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }
  
  return <LandingPage />;
};

// Public Route Component (redirect if authenticated)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, user } = useAuthStore();
  
  if (isAuthenticated) {
    if (user?.role === 'super_admin' || user?.role === 'personnel') return <Navigate to="/admin" replace />;
    if (user?.role === 'dealer') return <Navigate to="/dealer" replace />;
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

// Admin Route Guard
const AdminRoute = ({ children }) => {
  const { user } = useAuthStore();
  if (user?.role !== 'super_admin' && user?.role !== 'personnel') {
    return <Navigate to="/dashboard" replace />;
  }
  return children;
};

// Super Admin Only Route Guard
const SuperAdminRoute = ({ children }) => {
  const { user } = useAuthStore();
  if (user?.role !== 'super_admin') {
    return <Navigate to="/admin" replace />;
  }
  return children;
};

// Dealer Route Guard
const DealerRoute = ({ children }) => {
  const { user } = useAuthStore();
  if (user?.role !== 'dealer') {
    return <Navigate to="/" replace />;
  }
  return children;
};

function App() {
  return (
    <ThemeProvider>
    <I18nProvider>
    <BrowserRouter>
      <BrandProvider>
      <Toaster 
        position="top-right" 
        richColors 
        closeButton
        toastOptions={{
          duration: 4000,
        }}
      />
      <Routes>
        {/* Landing Page */}
        <Route path="/" element={<LandingRoute />} />
        
        {/* Public Routes */}
        <Route
          path="/login"
          element={
            <PublicRoute>
              <LoginPage />
            </PublicRoute>
          }
        />
        <Route path="/register" element={<Navigate to="/login" replace />} />
        <Route path="/barkod" element={<BarcodeSearchPage />} />
        <Route path="/legal/:tab" element={<LegalPage />} />
        <Route path="/legal" element={<LegalPage />} />

        {/* Protected Routes with Layout */}
        <Route
          element={
            <ProtectedRoute>
              <MainLayout />
            </ProtectedRoute>
          }
        >
          <Route path="dashboard" element={<ExpiredRouteGuard><DashboardPage /></ExpiredRouteGuard>} />
          <Route path="customers" element={<ExpiredRouteGuard><CustomersPage /></ExpiredRouteGuard>} />
          <Route path="payments" element={<Navigate to="/admin/payment-tracking" replace />} />
          <Route path="collected" element={<Navigate to="/admin/payment-tracking" replace />} />
          <Route path="whatsapp" element={<ExpiredRouteGuard><WhatsAppPage /></ExpiredRouteGuard>} />
          <Route path="personnel" element={<ExpiredRouteGuard><PersonnelPage /></ExpiredRouteGuard>} />
          <Route path="settings" element={<ExpiredRouteGuard allowWhenExpired><SettingsPage /></ExpiredRouteGuard>} />
          <Route path="profile" element={<ExpiredRouteGuard allowWhenExpired><ProfilePage /></ExpiredRouteGuard>} />

          {/* Admin Routes */}
          <Route path="admin" element={<AdminRoute><AdminDashboardPage /></AdminRoute>} />
          <Route path="admin/dealers" element={<AdminRoute><AdminDealersPage /></AdminRoute>} />
          <Route path="admin/products" element={<AdminRoute><AdminProductsPage /></AdminRoute>} />
          <Route path="admin/orders" element={<AdminRoute><AdminOrdersPage /></AdminRoute>} />
          <Route path="admin/payment-tracking" element={<AdminRoute><AdminPaymentTrackingPage /></AdminRoute>} />
          <Route path="admin/whatsapp" element={<AdminRoute><AdminWhatsAppPage /></AdminRoute>} />
          <Route path="admin/personnel" element={<SuperAdminRoute><AdminPersonnelPage /></SuperAdminRoute>} />
          <Route path="admin/integrations" element={<AdminRoute><AdminIntegrationsPage /></AdminRoute>} />
          <Route path="admin/security" element={<AdminRoute><AdminSecurityPage /></AdminRoute>} />
          <Route path="admin/settings" element={<AdminRoute><AdminSettingsPage /></AdminRoute>} />
          <Route path="admin/shop-settings" element={<AdminRoute><AdminShopSettingsPage /></AdminRoute>} />
          {/* Legacy redirects */}
          <Route path="admin/companies" element={<Navigate to="/admin" replace />} />
          <Route path="admin/packages" element={<Navigate to="/admin" replace />} />
          <Route path="admin/users" element={<Navigate to="/admin/dealers" replace />} />
          <Route path="admin/messages" element={<Navigate to="/admin/whatsapp" replace />} />
          <Route path="admin/templates" element={<Navigate to="/admin/whatsapp" replace />} />
          <Route path="admin/payment-methods" element={<Navigate to="/admin/integrations" replace />} />
        </Route>

        {/* Dealer Routes with DealerLayout */}
        <Route
          element={
            <ProtectedRoute>
              <DealerRoute>
                <DealerLayout />
              </DealerRoute>
            </ProtectedRoute>
          }
        >
          <Route path="dealer" element={<DealerDashboard />} />
          <Route path="dealer/shop" element={<DealerShop />} />
          <Route path="dealer/cart" element={<DealerCart />} />
          <Route path="dealer/orders" element={<DealerOrders />} />
          <Route path="dealer/payments" element={<DealerPayments />} />
          <Route path="dealer/account" element={<DealerAccount />} />
          <Route path="dealer/profile" element={<ProfilePage />} />
        </Route>

        {/* Payment Result Pages */}
        <Route path="payment/success" element={<PaymentResultPage status="success" />} />
        <Route path="payment/fail" element={<PaymentResultPage status="fail" />} />

        {/* Catch all */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      </BrandProvider>
    </BrowserRouter>
    </I18nProvider>
    </ThemeProvider>
  );
}

export default App;
